import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "../ui/card";
import {
  FileText,
  MessageSquare,
  Mail,
  Users,
  Home,
  Building2,
  Wrench,
  ArrowUpRight,
} from "lucide-react";
import { adminApi } from "../../lib/adminApi";

const projectTypeIcons = {
  residential: Home,
  commercial: Building2,
  renovation: Wrench,
};

const getStatusClass = (status) => {
  if (status === "new") return "bg-yellow-100 text-yellow-800";
  if (status === "contacted") return "bg-blue-100 text-blue-800";
  if (status === "won") return "bg-green-100 text-green-800";
  return "bg-gray-100 text-gray-800";
};

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentQuotes, setRecentQuotes] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, quotesRes] = await Promise.all([
        adminApi.get("/admin/dashboard/stats"),
        adminApi.get("/admin/quotes?limit=5"),
      ]);
      setStats(statsRes.data);
      setRecentQuotes(quotesRes.data.quotes);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Leads",
      value: stats?.totals?.total_leads || 0,
      icon: Users,
      color: "bg-blue-500",
      change: stats?.recent_activity?.quotes_7_days || 0,
      changeLabel: "new this week",
    },
    {
      title: "Quote Requests",
      value: stats?.totals?.quotes || 0,
      icon: FileText,
      color: "bg-brand-orange",
      link: "/admin/quotes",
    },
    {
      title: "Contact Messages",
      value: stats?.totals?.contacts || 0,
      icon: MessageSquare,
      color: "bg-green-500",
      link: "/admin/contacts",
    },
    {
      title: "Newsletter Subs",
      value: stats?.totals?.newsletter || 0,
      icon: Mail,
      color: "bg-purple-500",
      link: "/admin/newsletter",
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-gray-900 uppercase">
          Dashboard
        </h1>
        <p className="text-gray-600 mt-1">
          Welcome back! Here's an overview of your leads and activity.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className="p-6 bg-white border-0 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {stat.value}
                  </p>
                  {stat.change !== undefined && (
                    <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                      <ArrowUpRight className="w-4 h-4" />
                      {stat.change} {stat.changeLabel}
                    </p>
                  )}
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              {stat.link && (
                <Link
                  to={stat.link}
                  className="inline-flex items-center text-sm text-brand-orange hover:underline mt-4"
                >
                  View all <ArrowUpRight className="w-4 h-4 ml-1" />
                </Link>
              )}
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quotes by Type */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-gray-900 uppercase mb-6">
            Quotes by Project Type
          </h2>
          <div className="space-y-4">
            {Object.entries(stats?.quotes_by_type || {}).map(([type, count]) => {
              const Icon = projectTypeIcons[type] || FileText;
              const total = stats?.totals?.quotes || 1;
              const percentage = Math.round((count / total) * 100) || 0;
              return (
                <div key={type} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {type}
                      </span>
                      <span className="text-sm text-gray-500">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-orange rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Quote Status */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-gray-900 uppercase mb-6">
            Quote Status Overview
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">
                {stats?.quotes_by_status?.new || 0}
              </p>
              <p className="text-sm text-yellow-700 mt-1">New / Pending</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-600">
                {stats?.quotes_by_status?.contacted || 0}
              </p>
              <p className="text-sm text-green-700 mt-1">Contacted</p>
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
              Monthly Trend
            </h3>
            <div className="flex items-end justify-between h-24 gap-2">
              {(() => {
                const maxQuotes = Math.max(...(stats?.monthly_quotes || []).map(m => m.quotes), 1);
                return stats?.monthly_quotes?.map((month) => {
                const barHeight = Math.max((month.quotes / maxQuotes) * 80, 4);
                return (
                  <div key={month.month} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-brand-orange rounded-t transition-all duration-500"
                      style={{ height: `${barHeight}px` }}
                    />
                    <span className="text-xs text-gray-500 mt-2">{month.month}</span>
                  </div>
                );
              });
              })()}
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Quotes */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-lg font-bold text-gray-900 uppercase">
            Recent Quote Requests
          </h2>
          <Link
            to="/admin/quotes"
            className="text-sm text-brand-orange hover:underline"
          >
            View all →
          </Link>
        </div>

        {recentQuotes.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No quote requests yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-3">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-3">Project Type</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-3">Budget</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentQuotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="py-4">
                      <div>
                        <p className="font-medium text-gray-900">{quote.full_name}</p>
                        <p className="text-sm text-gray-500">{quote.email}</p>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                        {quote.project_type}
                      </span>
                    </td>
                    <td className="py-4 text-sm text-gray-600">{quote.budget_range || "-"}</td>
                    <td className="py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(quote.status)}`}>
                        {quote.status || "new"}
                      </span>
                    </td>
                    <td className="py-4 text-sm text-gray-500">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
